import { ImageFromCdn, Typography } from '@/components'
import { Breakpoint, ThemeColor } from '@/enums'
import {
  themeBreakpointDown,
  themeBreakpointUp,
  themeColor,
  toThemePixels,
  useIsMobile,
} from '@/utils'
import { Button } from '@surein/ui'
import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { prop } from 'styled-tools'
import { OptimiseCoverageModal } from './OptimiseCoverageModal'

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  border-radius: 8px;
  border: 1px solid ${themeColor(ThemeColor.b30)};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  background-color: #f8f8f8;
  gap: 2rem;
  overflow: hidden;

  ${themeBreakpointDown(Breakpoint.sm)} {
    align-items: center;
    justify-content: center;
    flex-direction: column-reverse;
    gap: 0;
  }
`

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 8px;
  max-width: 400px;
  align-items: flex-start;
  justify-content: space-around;

  ${themeBreakpointDown(Breakpoint.sm)} {
    max-width: 100%;
    padding: ${toThemePixels(6)} ${toThemePixels(8)} ${toThemePixels(6)};
  }
`

const Heading = styled.h4`
  font-size: 20px;

  ${themeBreakpointDown(Breakpoint.sm)} {
    font-size: 16px;
  }
`

const ImageWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  position: relative;

  ${themeBreakpointDown(Breakpoint.sm)} {
    width: 100%;
    height: 140px;
  }

  img {
    object-fit: cover;
    height: 100%;
    max-width: 150px;
    width: auto;

    ${themeBreakpointUp(Breakpoint['3xl'])} {
      max-width: 250px;
    }

    ${themeBreakpointDown(Breakpoint.sm)} {
      max-width: 100%;
      object-fit: cover;
      object-position: ${prop('backgroundImageX', 'center')}
        ${prop('backgroundImageY', 'center')};
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      overflow: hidden;
      height: 100%;
      width: 100%;
    }
  }
`

interface OptimiseCoverCardProps {
  locationId: string
}

export const OptimiseCoverCard = ({ locationId }: OptimiseCoverCardProps) => {
  const { t } = useTranslation()
  const { isMobile } = useIsMobile(Breakpoint.lg)

  const [isOpen, setIsOpen] = useState(false)
  return (
    <>
      <Container>
        <TextSection>
          <Typography bold variant="h4">
            {t('yearlyAssessment.optimizeCoverCard-title')}
          </Typography>
          <Heading>
            <Typography variant={isMobile ? 'p1Body' : 'h4'}>
              {t('yearlyAssessment.optimiseCoverageModal-title')}
            </Typography>
          </Heading>
          <Button
            onClick={() => setIsOpen(true)}
            suffixIcon={ArrowRight}
            variant="secondary"
            shape="square"
          >
            <Typography bold variant="p1Body" />
            {t('yearlyAssessment.optimiseCoverCard-button-label')}
          </Button>
        </TextSection>
        <ImageWrapper>
          <ImageFromCdn
            fallbackType="jpg"
            name="fire-extinguisher"
            type="webp"
          />
        </ImageWrapper>
      </Container>
      <OptimiseCoverageModal
        isOpen={isOpen}
        locationId={locationId}
        onClose={() => setIsOpen(false)}
      />
    </>
  )
}
